import { Typography, Select, MenuItem, FormControl } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChatFlowHeadersTable from 'components/@home/@settings/Tabs/ChatFlowHeadersTable';
import TextField from 'components/formik/TextField';
import { Field, useFormikContext } from 'formik';
import { bool } from 'prop-types';
import React, { useMemo } from 'react';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  description: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  select: {
    marginBottom: theme.spacing(2),
  },
});

// Custom FormikSelect component
const FormikSelect = ({ field, form, children, sx, ...props }) => {
  const { name, value } = field;
  const { errors, submitCount, setFieldValue } = form;

  return (
    <FormControl fullWidth error={!!(errors[name] && submitCount > 0)} sx={sx}>
      <Select
        {...props}
        name={name}
        value={value || 'none'}
        onChange={e => setFieldValue(name, e.target.value)}
      >
        {children}
      </Select>
    </FormControl>
  );
};

const CustomWebhookConfig = ({ isZapier = false }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const { values } = useFormikContext();
  const urlFieldName = isZapier ? 'configuration.zapierWebhookUrl' : 'configuration.url';
  const urlLabel = isZapier ? 'Zapier Webhook URL' : 'Webhook URL';

  const authTypeOptions = [
    { value: 'none', label: I18n.t('ChatFlowSettings.No authentication') },
    { value: 'bearer', label: I18n.t('ChatFlowSettings.Bearer token') },
    { value: 'basic', label: I18n.t('ChatFlowSettings.Basic authentication') },
  ];

  const authType = values?.configuration?.authentication?.type || 'none';

  return (
    <>
      <Typography sx={styles.heading}>{I18n.t(`ChatFlowSettings.${urlLabel}`)}</Typography>
      <Typography sx={styles.description}>
        {isZapier
          ? I18n.t('ChatFlowSettings.Zapier webhook URL')
          : I18n.t('ChatFlowSettings.HTTPS URL to send webhook to')}
      </Typography>
      <Field
        component={TextField}
        name={urlFieldName}
        type="url"
        pattern="https://.*"
        placeholder="https://..."
      />

      {!isZapier && (
        <>
          <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Timeout')}</Typography>
          <Typography sx={styles.description}>
            {I18n.t('ChatFlowSettings.Request timeout in seconds (1-30)')}
          </Typography>
          <Field component={TextField} name="configuration.timeout" type="number" placeholder="5" />

          <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Authentication')}</Typography>
          <Typography sx={styles.description}>
            {I18n.t('ChatFlowSettings.Authentication caption')}
          </Typography>

          <Typography sx={styles.heading}>
            {I18n.t('ChatFlowSettings.Authentication type')}
          </Typography>
          <Field
            component={FormikSelect}
            name="configuration.authentication.type"
            sx={styles.select}
          >
            {authTypeOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Field>

          {authType === 'basic' && (
            <>
              <Field
                component={TextField}
                name="configuration.authentication.username"
                placeholder={I18n.t('ChatFlowSettings.Login username')}
              />
              <br />
              <br />
              <Field
                component={TextField}
                name="configuration.authentication.password"
                type="password"
                placeholder={I18n.t('ChatFlowSettings.Login password')}
              />
            </>
          )}

          {authType === 'bearer' && (
            <Field
              component={TextField}
              name="configuration.authentication.token"
              placeholder={I18n.t('ChatFlowSettings.Bearer token placeholder')}
            />
          )}

          <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Headers')}</Typography>
          <Typography sx={styles.description}>
            {I18n.t('ChatFlowSettings.Optional HTTP headers')}
          </Typography>
          <ChatFlowHeadersTable name="configuration.headers" />
        </>
      )}
    </>
  );
};

CustomWebhookConfig.propTypes = {
  isZapier: bool,
};

export default CustomWebhookConfig;
